import React, { useState } from 'react';
import { reviewsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Star, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ReviewForm = ({ 
  isOpen, 
  onClose, 
  appointment,
  onReviewSubmitted 
}) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Veuillez sélectionner une note');
      return;
    }

    setLoading(true);
    try {
      await reviewsAPI.create({
        appointment_id: appointment.id,
        professional_id: appointment.professional_id,
        rating,
        comment: comment.trim() || null,
      });

      toast.success('Avis enregistré avec succès !');
      onReviewSubmitted && onReviewSubmitted();
      handleClose();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setHoveredRating(0);
    setComment('');
    onClose();
  };

  const ratingLabels = {
    1: 'Très insatisfait',
    2: 'Insatisfait',
    3: 'Correct',
    4: 'Satisfait',
    5: 'Très satisfait',
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle 
            className="text-xl text-[#1A237E]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Évaluez votre consultation
          </DialogTitle>
          <DialogDescription>
            Comment s'est passée votre consultation avec Dr. {appointment?.professional_name} ?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Star Rating */}
          <div className="flex flex-col items-center mb-6">
            <div className="flex gap-2 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110 focus:outline-none"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`w-10 h-10 transition-colors ${
                      star <= (hoveredRating || rating)
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-slate-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            {(hoveredRating || rating) > 0 && (
              <span className="text-sm font-medium text-[#1A237E]">
                {ratingLabels[hoveredRating || rating]}
              </span>
            )}
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#1A237E]">
              Commentaire (optionnel)
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Partagez votre expérience..."
              className="min-h-[100px] rounded-xl resize-none"
              maxLength={500}
              data-testid="review-comment"
            />
            <p className="text-xs text-slate-500 text-right">
              {comment.length}/500 caractères
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex-1 rounded-xl"
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || rating === 0}
            className="flex-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-xl"
            data-testid="submit-review-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Envoi...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Envoyer
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewForm;
